import { useCallback, useState } from 'react';

type ActiveModal = 'clear' | 'regionChange' | null;

export type UseMarketConfirmationModalProps = {
  hasSegments: boolean;
  setSelectedRegion: (code: string | null) => void;
  changeRegionAndClearSegments: (code: string | null) => void;
  clearRegion: () => void;
  onToggleExpand: () => void;
  onCollapseCriteriaSections: () => void;
};

export type UseMarketConfirmationModalReturn = {
  activeModal: ActiveModal;
  pendingRegionCode: string | null;
  openClearModal: (event: React.MouseEvent<HTMLButtonElement>) => void;
  handleRegionChangeWithModal: (event: React.SyntheticEvent, code: string | null) => void;
  closeModal: () => void;
  confirmModal: () => void;
};

export const useMarketConfirmationModal = ({
  hasSegments,
  setSelectedRegion,
  changeRegionAndClearSegments,
  clearRegion,
  onToggleExpand,
  onCollapseCriteriaSections,
}: UseMarketConfirmationModalProps): UseMarketConfirmationModalReturn => {
  const [activeModal, setActiveModal] = useState<ActiveModal>(null);
  const [pendingRegionCode, setPendingRegionCode] = useState<string | null>(null);

  const openClearModal = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setActiveModal('clear');
  }, []);

  const handleRegionChangeWithModal = useCallback(
    (_event: React.SyntheticEvent, code: string | null) => {
      if (!hasSegments || code === null) {
        setSelectedRegion(code);
        return;
      }

      setPendingRegionCode(code);
      setActiveModal('regionChange');
    },
    [hasSegments, setSelectedRegion]
  );

  const closeModal = useCallback(() => {
    setActiveModal(null);
    setPendingRegionCode(null);
  }, []);

  const confirmModal = useCallback(() => {
    if (activeModal === 'clear') {
      clearRegion();
      onToggleExpand();
    } else if (activeModal === 'regionChange') {
      changeRegionAndClearSegments(pendingRegionCode);
      onCollapseCriteriaSections();
    }
    setActiveModal(null);
    setPendingRegionCode(null);
  }, [
    activeModal,
    clearRegion,
    changeRegionAndClearSegments,
    pendingRegionCode,
    onToggleExpand,
    onCollapseCriteriaSections,
  ]);

  return {
    activeModal,
    pendingRegionCode,
    openClearModal,
    handleRegionChangeWithModal,
    closeModal,
    confirmModal,
  };
};
